import { supabase } from '../../config/supabase';
import { FINAL_PRICING_DEPARTMENTS } from './final-pricings';
import { createJobActionLog } from './activityLog';
import { computeEstimationTotals, computeEffectivePricing, isDepartmentQuotable } from './finalPricingModes';

export const QUOTATION_DEPARTMENTS = FINAL_PRICING_DEPARTMENTS;

const LIST_PAGE_SIZE = 50;
// Safety cap on how many underlying DB pages a single loadMore scan will
// walk through while hunting for priced rows (mirrors final-pricings.js).
const MAX_SCAN_PAGES = 10;

const SUPPORTED_DEPARTMENTS = QUOTATION_DEPARTMENTS.map(department => department.key);

const PRICING_SELECT = `
    id,
    company_id,
    job_id,
    department,
    final_price,
    notes,
    created_by,
    created_by_name,
    created_at,
    updated_by,
    updated_by_name,
    updated_at
`;

// Includes customer_mail (unlike final-pricings.js's list select) because
// QuotationEmailSender pre-fills the "Send to" field straight from the row
// data already loaded here — no extra per-job fetch when the modal opens.
const LIST_JOB_SELECT_BASE = `
    id,
    company_id,
    unique_id,
    customer_name,
    customer_mail,
    customer_address,
    job_type,
    is_cancelled,
    created_at,
    job_final_pricings(${PRICING_SELECT})
`;

// job_pricing_entries is always selected in full — every company's jobs
// need computeEffectivePricing's fallback data now, not just companies
// that had opted into an estimation-based mode.
const LIST_JOB_SELECT = `
    ${LIST_JOB_SELECT_BASE},
    job_pricing_entries(*)
`;

const normalizeFinalPricings = value => {
    const records = Array.isArray(value)
        ? value
        : value
            ? [value]
            : [];

    return records.reduce(
        (result, pricing) => {
            if (
                pricing?.department &&
                SUPPORTED_DEPARTMENTS.includes(pricing.department)
            ) {
                result[pricing.department] = pricing;
            }

            return result;
        },
        {
            roof: null,
            wall: null,
            floor: null,
        }
    );
};

const normalizeJob = job => {
    const final_pricings = normalizeFinalPricings(job.job_final_pricings);
    const pricing_entries = job.job_pricing_entries || [];
    const estimationTotals = computeEstimationTotals(pricing_entries);

    return {
        ...job,
        address: job.customer_address ?? job.address ?? null,
        final_pricings,
        pricing_entries,
        effective_pricing: computeEffectivePricing({ finalPricings: final_pricings, estimationTotals }),
    };
};

const sanitizeSearchTerm = value =>
    String(value || '').trim().replace(/[,()%]/g, ' ').trim();

const runQuotationQuery = async (companyId, {
    from,
    to,
    search,
    jobType,
    cancelledStatus,
    sortBy,
}) => {
    let query = supabase
        .from('jobs')
        .select(LIST_JOB_SELECT, { count: 'exact' })
        .eq('company_id', companyId)
        .eq('deleted', false);

    const term = sanitizeSearchTerm(search);
    if (term) {
        // job_type is deliberately excluded here — it's a Postgres enum
        // column with no ILIKE/~~* operator of its own (enums only support
        // equality/ordering), and PostgREST's `or=(...)` logic-tree grammar
        // doesn't accept a `column::type` cast to work around that (only a
        // top-level filter param does). Exact-match job type filtering is
        // already available separately via the jobType dropdown filter
        // below.
        query = query.or(
            `unique_id.ilike.%${term}%,customer_name.ilike.%${term}%,customer_address.ilike.%${term}%`
        );
    }

    if (jobType && jobType !== 'all') {
        query = query.eq('job_type', jobType);
    }

    if (cancelledStatus === 'active') {
        query = query.eq('is_cancelled', false);
    } else if (cancelledStatus === 'cancelled') {
        query = query.eq('is_cancelled', true);
    }

    switch (sortBy) {
        case 'oldest':
            query = query.order('created_at', { ascending: true });
            break;
        case 'alphabetical':
            query = query.order('unique_id', { ascending: true });
            break;
        case 'reverse_alphabetical':
            query = query.order('unique_id', { ascending: false });
            break;
        default:
            // 'newest' and the price-based sorts (no backing DB column) fall
            // back to created_at desc; price sorts are re-applied
            // client-side on whatever rows are currently loaded.
            query = query.order('created_at', { ascending: false });
    }

    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;

    return {
        rows: (data || []).map(normalizeJob),
        total: count || 0,
    };
};

/**
 * Fetch one "page" of quotation rows.
 *
 * `viewMode: 'separate'` — one row per (job, department) pair, only for
 * departments that have a final price. Jobs with zero priced departments
 * contribute nothing.
 *
 * `viewMode: 'combined'` — one row per job that has at least one priced
 * department; if `showPartial` is false, only jobs where all 3 departments
 * are priced qualify.
 *
 * "Has pricing" isn't a plain DB column, so — same as
 * getFinalPricingJobsPage in final-pricings.js — this scans forward through
 * DB pages (bounded by MAX_SCAN_PAGES) collecting matches, advancing the
 * returned `nextOffset` by exactly the number of raw job rows consumed so
 * nothing is skipped or double-counted across paginated calls.
 */
export const getQuotationRowsPage = async (companyId, {
    offset = 0,
    limit = LIST_PAGE_SIZE,
    search = '',
    jobType = 'all',
    cancelledStatus = 'active',
    sortBy = 'newest',
    viewMode = 'separate',
    showPartial = true,
    requireFinalPricing = true,
} = {}) => {
    if (!companyId) {
        return { data: [], nextOffset: offset, hasMore: false, total: 0 };
    }

    const matches = [];
    let cursor = offset;
    let dbExhausted = false;

    for (let scanned = 0; scanned < MAX_SCAN_PAGES && matches.length < limit; scanned += 1) {
        const { rows, total: rowTotal } = await runQuotationQuery(companyId, {
            from: cursor,
            to: cursor + limit - 1,
            search,
            jobType,
            cancelledStatus,
            sortBy,
        });

        let consumed = 0;
        for (const job of rows) {
            consumed += 1;

            const jobMatches = viewMode === 'separate'
                ? QUOTATION_DEPARTMENTS
                    .filter(department => isDepartmentQuotable(job.effective_pricing.departments[department.key], requireFinalPricing))
                    .map(department => ({
                        rowId: `${job.id}:${department.key}`,
                        job,
                        department: department.key,
                        departmentLabel: department.label,
                        pricing: {
                            final_price: job.effective_pricing.departments[department.key].amount,
                            notes: job.effective_pricing.departments[department.key].notes,
                            source: job.effective_pricing.departments[department.key].source,
                        },
                    }))
                : (() => {
                    const quotableKeys = QUOTATION_DEPARTMENTS.filter(
                        department => isDepartmentQuotable(job.effective_pricing.departments[department.key], requireFinalPricing)
                    );
                    const hasAny = quotableKeys.length > 0;
                    const hasAll = quotableKeys.length === QUOTATION_DEPARTMENTS.length;
                    return (hasAny && (showPartial || hasAll)) ? [{ rowId: job.id, job }] : [];
                })();

            matches.push(...jobMatches);
            if (matches.length >= limit) break;
        }

        cursor += consumed;

        if (rows.length === 0 || cursor >= rowTotal) {
            dbExhausted = true;
            break;
        }
    }

    return {
        data: matches,
        nextOffset: cursor,
        hasMore: !dbExhausted,
        // "Has pricing" is always a post-filter here, so an exact remaining
        // count isn't knowable — same as final-pricings.js when its
        // pricingStatus filter is active.
        total: null,
    };
};

/**
 * Fetch a single job in the same normalized shape as getQuotationRowsPage's
 * rows, for the "Go to Quotation" email deep link (`/quotations?jobId=…`) —
 * that job may not be on whatever page/filter the list happens to be on, so
 * this looks it up directly by id instead of paging through the list to find
 * it.
 */
export const getQuotationJobById = async (companyId, jobId) => {
    if (!companyId || !jobId) return null;

    const { data, error } = await supabase
        .from('jobs')
        .select(LIST_JOB_SELECT)
        .eq('company_id', companyId)
        .eq('id', jobId)
        .eq('deleted', false)
        .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return normalizeJob(data);
};

/**
 * Departments already emailed to the customer for this job, derived from
 * customer_conversations rows written by sendQuotationEmail below.
 */
export const getSentQuotationDepartments = async (companyId, jobId) => {
    const { data, error } = await supabase
        .from('customer_conversations')
        .select('metadata')
        .eq('company_id', companyId)
        .eq('job_id', jobId)
        .eq('conversation_type', 'quotation_email')
        .eq('direction', 'outbound')
        .eq('status', 'sent');

    if (error) throw error;

    const sent = new Set();
    for (const row of data || []) {
        const departments = row?.metadata?.quoted_departments || [];
        if (Array.isArray(departments)) {
            departments.forEach(entry => {
                if (entry?.department) sent.add(entry.department);
            });
        }
    }

    return sent;
};

/**
 * Bulk version of getSentQuotationDepartments for a page of jobs at once —
 * used by the Quotation table to render "Sent" badges without an N+1 query
 * per visible row.
 */
export const getSentQuotationDepartmentsForJobs = async (companyId, jobIds) => {
    if (!companyId || !jobIds?.length) return {};

    // A wide view (e.g. a full year for a busy company) can carry many
    // hundreds/thousands of job ids — passing them all in one `.in()` filter
    // builds a URL long enough that the gateway rejects it outright with a
    // 400 Bad Request before the query ever reaches Postgres. Chunk the id
    // list so each request's URL stays a safe size, and merge the results.
    const CHUNK_SIZE = 200;
    const chunks = [];
    for (let i = 0; i < jobIds.length; i += CHUNK_SIZE) {
        chunks.push(jobIds.slice(i, i + CHUNK_SIZE));
    }

    const allRows = [];
    for (const chunk of chunks) {
        const { data, error } = await supabase
            .from('customer_conversations')
            .select('job_id, metadata')
            .eq('company_id', companyId)
            .eq('conversation_type', 'quotation_email')
            .eq('direction', 'outbound')
            .eq('status', 'sent')
            .in('job_id', chunk);

        if (error) throw error;
        allRows.push(...(data || []));
    }

    const result = {};
    for (const row of allRows) {
        const departments = row?.metadata?.quoted_departments || [];
        if (!Array.isArray(departments)) continue;

        const set = result[row.job_id] || new Set();
        departments.forEach(entry => {
            if (entry?.department) set.add(entry.department);
        });
        result[row.job_id] = set;
    }

    return result;
};

/**
 * Send a quotation email through the (separately deployed) Edge Function.
 * The Edge Function itself writes the customer_conversations row (subject,
 * body, provider message id, metadata.quoted_departments for "already sent"
 * tracking — mirrors send-booked-dates-email-postmark) so this only needs to
 * add the job_action_logs entry for the job's own activity trail. That write
 * is fire-and-forget-safe — errors are logged to console only, since the
 * email itself already went out by the time it runs.
 */
export const sendQuotationEmail = async ({
    companyId,
    jobId,
    shortenedJobId,
    to,
    cc,
    subject,
    content,
    html,
    contactUrl,
    logoUrl,
    companyName,
    customerName,
    departments,
    userProfile,
}) => {
    const { data, error } = await supabase.functions.invoke('send-quotation-email-postmark', {
        body: {
            jobId,
            shortenedJobId,
            companyId,
            to,
            cc,
            subject,
            content,
            html,
            departments,
            contactUrl,
            logoUrl,
            companyName,
        },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    const departmentSummary = departments
        .map(department => `${department.label} ($${department.final_price})`)
        .join(', ');

    try {
        await createJobActionLog({
            job_id: jobId,
            company_id: companyId,
            user_id: userProfile?.id || null,
            user_name:
                userProfile?.full_name ||
                userProfile?.username ||
                userProfile?.email ||
                'Unknown user',
            user_role: userProfile?.role || null,
            action: 'quotation_sent',
            action_origin: 'quotation',
            details: `Quotation emailed to ${customerName || 'customer'} — ${departmentSummary}`,
            new_values: { to, cc, departments },
        });
    } catch (logError) {
        console.error('[quotations] Failed to write job action log:', logError);
    }

    return data;
};

export const subscribeToQuotations = (companyId, callback) => {
    if (!companyId) return null;

    return supabase
        .channel(`quotations-${companyId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_final_pricings',
                filter: `company_id=eq.${companyId}`,
            },
            callback
        )
        .subscribe();
};

export const unsubscribeFromQuotations = async channel => {
    if (channel) {
        await supabase.removeChannel(channel);
    }
};
